import { ColDef, GridOptions } from 'ag-grid-community';
import moment from 'moment';

import { buildColDef, ColType } from '@app/shared/utils/ag-grid.col-def.utils';

import { AssemblyOrderActionCellRendererComponent } from '../components/assembly-order-action-cell-renderer/assembly-order-action-cell-renderer.component';
import { AssemblyOrderStatusCellEditorComponent } from '../components/assembly-order-status-cell-editor/assembly-order-status-cell-editor.component';
import {
  EAssemblyOrderAgField,
  EAssemblyOrderStatus,
  EAssemblyOrderType,
} from '../interfaces/assembly-order.interface';

const statusFormat :{ [key in EAssemblyOrderStatus] :string } = {
  [EAssemblyOrderStatus.COMPLETED]: 'Completed',
  [EAssemblyOrderStatus.DRAFT]: 'Draft',
  [EAssemblyOrderStatus.PAUSED]: 'Paused',
  [EAssemblyOrderStatus.IN_PROGRESS]: 'In Progress',
  [EAssemblyOrderStatus.ISSUED]: 'Issued',
};

const typeFormat :{ [key in EAssemblyOrderType] :string } = {
  [EAssemblyOrderType.ASSEMBLY]: 'Assembly',
  [EAssemblyOrderType.DISASSEMBLY]: 'Disassembly',
};

const defaultColumnDefs :ColDef[] = [
  {
    headerName: 'Order #',
    field: EAssemblyOrderAgField.ID,
    filter: 'agNumberColumnFilter',
    minWidth: 120, // override default
  },
  {
    headerName: 'Order Type',
    field: EAssemblyOrderAgField.ORDER_TYPE,
    valueFormatter: (params :any) => {
      if (!params.data.type) {
        return '';
      }

      return typeFormat[params.data.type as EAssemblyOrderType];
    },
    editable: false,
    filterParams: {
      values: [
        typeFormat[EAssemblyOrderType.ASSEMBLY],
        typeFormat[EAssemblyOrderType.DISASSEMBLY],
      ],
    },
  },
  buildColDef(
    ColType.DEFAULT,
    {
      headerName: 'SKU',
      field: EAssemblyOrderAgField.INTERNAL_SKU,
    },
    {
      routerLinkFormatter: ({ data }) => {
        if (!data?.rfq) {
          return '';
        }

        return `/inventory/${data.rfq.id}/view`;
      },
    },
  ),
  buildColDef(
    ColType.DEFAULT,
    {
      headerName: 'Product Name',
      field: EAssemblyOrderAgField.PRODUCT_NAME,
    },
    {
      routerLinkFormatter: ({ data }) => {
        if (!data?.rfq) {
          return '';
        }

        return `/inventory/${data.rfq.id}/view`;
      },
    },
  ),
  {
    headerName: 'Age',
    field: EAssemblyOrderAgField.AGE,
    filter: false,
    filterParams: {
      filterOptions: ['equals', 'inRange'],
      defaultOption: 'equals',
    },
    valueFormatter: (params :any) => {
      if (params.data.age) {
        const minutesAgo = params.data.age; // example value
        const time = moment().subtract(minutesAgo, 'minutes');

        return time.fromNow();
      }

      return '';
    },
  },
  {
    headerName: 'Status',
    field: EAssemblyOrderAgField.STATUS,
    valueFormatter: (params :any) => {
      if (!params.data.status) {
        return '';
      }

      return statusFormat[params.data.status as EAssemblyOrderStatus];
    },
    editable: true,
    cellEditorFramework: AssemblyOrderStatusCellEditorComponent,
  },
  {
    headerName: 'Planned Quantity',
    field: EAssemblyOrderAgField.PLANNED_QUANTITY,
    filter: 'agNumberColumnFilter',
    filterParams: {
      filterOptions: ['equals', 'inRange'],
      defaultOption: 'equals',
    },
    valueFormatter: (params :any) => {
      return `${params.data.plannedQuantity} ${params.data.plannedUom.name}`;
    },
  },
  {
    headerName: 'Alternate IDs',
    field: EAssemblyOrderAgField.ALTERNATE_IDS,
    filter: 'agTextColumnFilter',
    filterParams: {
      caseSensitive: false,
      filterOptions: ['contains'],
      defaultOption: 'contains',
    },
    valueGetter: (params :any) => {
      if (!params.data.alternateIds?.length) return '';
      return params.data.alternateIds
        .map((data) => (data.alternateId || '').toLowerCase().trim())
        .filter(Boolean)
        .join(', ');
    },
    cellRenderer: (params :any) => {
      if (!params.data.alternateIds?.length) return '';
      return params.data.alternateIds
        .map((data) => `<span class="lum-chip lum-chip--label lum-chip--small mr-1">${data.alternateId}</span>`)
        .join('');
    },
    minWidth: 150,
  },
  {
    headerName: 'Actual Quantity',
    field: EAssemblyOrderAgField.ACTUAL_QUANTITY,
    hide: true,
    filter: 'agNumberColumnFilter',
    filterParams: {
      filterOptions: ['equals', 'inRange'],
      defaultOption: 'equals',
    },
    valueFormatter: (params :any) => {
      return `${params.data.actualQuantity} ${params.data.actualUom.name}`;
    },
  },
  {
    headerName: 'Start Time',
    field: EAssemblyOrderAgField.START_TIME,
    hide: true,
    filter: 'agDateColumnFilter',
    filterParams: {
      filterOptions: ['equals'],
      defaultOption: 'equals',
    },
    valueFormatter: (params :any) => {
      if (params?.data?.startTime) {
        const date = moment(params?.data?.startTime);

        return date.format('MMM D, YYYY h:mm A');
      }

      return '';
    },
  },
  {
    headerName: 'End Time',
    field: EAssemblyOrderAgField.END_TIME,
    hide: true,
    filter: 'agDateColumnFilter',
    filterParams: {
      filterOptions: ['equals'],
      defaultOption: 'equals',
    },
    valueFormatter: (params :any) => {
      if (params?.data?.endTime) {
        const date = moment(params?.data?.endTime);

        return date.format('MMM D, YYYY h:mm A');
      }

      return '';
    },
  },
  {
    headerName: 'Actions',
    field: EAssemblyOrderAgField.ACTIONS,
    filter: false,
    pinned: 'right',
    cellClass: 'no-border-highlight',
    cellRenderer: AssemblyOrderActionCellRendererComponent,
    minWidth: 120, // override default
    width: 200,
  },
];

const openOrdersColumnDefs :ColDef[] = defaultColumnDefs.map(
  (columnDef :ColDef) => {
    if (columnDef.field === EAssemblyOrderAgField.STATUS) {
      return {
        ...columnDef,
        filterParams: {
          values: ['Draft', 'Issued', 'In Progress', 'Paused'],
        },
      };
    }

    return columnDef;
  },
);

const closedOrdersColumnDefs :ColDef[] = defaultColumnDefs.map(
  (columnDef :ColDef) => {
    if (columnDef.field === EAssemblyOrderAgField.STATUS) {
      return {
        ...columnDef,
        editable: false,
        filterParams: {
          values: ['Completed'],
        },
      };
    }

    return columnDef;
  },
);

const gridOptions :GridOptions = {
  rowModelType: 'serverSide',
  pagination: true,
  paginationPageSize: 100,
  suppressScrollOnNewData: true,
  defaultColDef: {
    sortable: false,
    flex: 1,
    filter: true,
    floatingFilter: true,
    filterParams: {
      filterOptions: ['equals'],
      defaultOption: 'equals',
      maxNumConditions: 1,
      debounceMs: 1000,
    },
    minWidth: 150,
  },
  sideBar: {
    toolPanels: [
      {
        id: 'columns',
        labelDefault: 'Columns',
        labelKey: 'columns',
        iconKey: 'columns',
        toolPanel: 'agColumnsToolPanel',
        toolPanelParams: {
          suppressValues: true,
          suppressPivots: true,
          suppressPivotMode: true,
          suppressRowGroups: true,
        },
      },
    ],
    defaultToolPanel: '',
  },
};

export const AssemblyOrderConfig = {
  openOrdersColumnDefs,
  closedOrdersColumnDefs,
  gridOptions,
};
